import {
  genericVertexShaderSource,
  blackRectFragmentShaderSource,
  blackCircleFragmentShaderSource,
  mosaicRectFragmentShaderSource,
  mosaicCircleFragmentShaderSource,
  blurRectFragmentShaderSource,
  blurCircleFragmentShaderSource,
  CCRect,
  CCMosaicRect,
  CCBlurRect,
  CanvasBaseObject,
} from '../../domain/content-control/canvas';
import { CanvasRectangle } from '../../domain/content-control/canvas/cc-rect';

type TransformFn = (rect: CCRect) => number[];

export abstract class EffectProgram {
  protected _program: WebGLProgram;
  protected _vao: WebGLVertexArrayObject;

  constructor(
    protected _gl: WebGL2RenderingContext,
    fragmentSource: string,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    const vertexShader = this._createShader(this._gl.VERTEX_SHADER, genericVertexShaderSource);
    const fragmentShader = this._createShader(this._gl.FRAGMENT_SHADER, fragmentSource);
    this._program = this._createProgram(vertexShader, fragmentShader);
    this._vao = setupVao(this._program);
  }

  get program(): WebGLProgram {
    return this._program;
  }

  get vao(): WebGLVertexArrayObject {
    return this._vao;
  }

  protected _getUniform(name: string): WebGLUniformLocation {
    return this._gl.getUniformLocation(this._program, name);
  }

  abstract setupUniforms(
    items: ReadonlyArray<CanvasBaseObject>,
    transformPosition: TransformFn,
    transformSize: TransformFn,
    calculateGaussianKernel?: (maxRadius: number) => Float32Array,
  ): void;

  private _createShader(type: GLenum, source: string): WebGLShader | null {
    const shader = this._gl.createShader(type);
    this._gl.shaderSource(shader, source);
    this._gl.compileShader(shader);
    if (!this._gl.getShaderParameter(shader, this._gl.COMPILE_STATUS)) {
      console.error(this._gl.getShaderInfoLog(shader));
      this._gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private _createProgram(
    vertexShader: WebGLShader | null,
    fragmentShader: WebGLShader | null,
  ): WebGLProgram | null {
    const program = this._gl.createProgram();
    this._gl.attachShader(program, vertexShader);
    this._gl.attachShader(program, fragmentShader);
    this._gl.linkProgram(program);
    if (!this._gl.getProgramParameter(program, this._gl.LINK_STATUS)) {
      console.error(this._gl.getProgramInfoLog(program));
      this._gl.deleteProgram(program);
      return null;
    }
    this._gl.validateProgram(program);
    if (!this._gl.getProgramParameter(program, this._gl.VALIDATE_STATUS)) {
      console.error('Unable to validate the shader program:', this._gl.getProgramInfoLog(program));
      return null;
    }
    return program;
  }
}

export class BlackRectProgram extends EffectProgram {
  private _regionPositionsLocation: WebGLUniformLocation;
  private _regionSizesLocation: WebGLUniformLocation;
  private _numRectanglesLocation: WebGLUniformLocation;

  constructor(
    gl: WebGL2RenderingContext,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, blackRectFragmentShaderSource, setupVao);
    this._regionPositionsLocation = this._getUniform('u_regionPositions');
    this._regionSizesLocation = this._getUniform('u_regionSizes');
    this._numRectanglesLocation = this._getUniform('u_numRectangles');
  }

  setupUniforms(
    items: ReadonlyArray<CanvasRectangle>,
    transformPosition: TransformFn,
    transformSize: TransformFn,
  ): void {
    const positions = new Float32Array(items.flatMap(transformPosition));
    const sizes = new Float32Array(items.flatMap(transformSize));
    this._gl.uniform2fv(this._regionPositionsLocation, positions);
    this._gl.uniform2fv(this._regionSizesLocation, sizes);
    this._gl.uniform1i(this._numRectanglesLocation, items.length);
  }
}

export class BlackCircleProgram extends EffectProgram {
  private _positionsLocation: WebGLUniformLocation;
  private _sizesLocation: WebGLUniformLocation;
  private _numCirclesLocation: WebGLUniformLocation;

  constructor(
    gl: WebGL2RenderingContext,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, blackCircleFragmentShaderSource, setupVao);
    this._positionsLocation = this._getUniform('u_regionPositions');
    this._sizesLocation = this._getUniform('u_regionSizes');
    this._numCirclesLocation = this._getUniform('u_numRegions');
  }

  setupUniforms(
    items: ReadonlyArray<CCRect>,
    transformPosition: TransformFn,
    transformSize: TransformFn,
  ): void {
    const positions = new Float32Array(items.flatMap(transformPosition));
    const sizes = new Float32Array(items.flatMap(transformSize));
    this._gl.uniform2fv(this._positionsLocation, positions);
    this._gl.uniform2fv(this._sizesLocation, sizes);
    this._gl.uniform1i(this._numCirclesLocation, items.length);
  }
}

export class MosaicRectProgram extends EffectProgram {
  private _positionsLocation: WebGLUniformLocation;
  private _sizesLocation: WebGLUniformLocation;
  private _densitiesLocation: WebGLUniformLocation;
  private _numRegionsLocation: WebGLUniformLocation;

  constructor(
    gl: WebGL2RenderingContext,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, mosaicRectFragmentShaderSource, setupVao);
    this._positionsLocation = this._getUniform('u_positions');
    this._sizesLocation = this._getUniform('u_sizes');
    this._densitiesLocation = this._getUniform('u_densities');
    this._numRegionsLocation = this._getUniform('u_numRegions');
  }

  setupUniforms(
    items: ReadonlyArray<CCMosaicRect>,
    transformPosition: TransformFn,
    transformSize: TransformFn,
  ): void {
    const positions = new Float32Array(items.flatMap(transformPosition));
    const sizes = new Float32Array(items.flatMap(transformSize));
    const densities = new Float32Array(items.map((rect) => rect.density));
    this._gl.uniform2fv(this._positionsLocation, positions);
    this._gl.uniform2fv(this._sizesLocation, sizes);
    this._gl.uniform1fv(this._densitiesLocation, densities);
    this._gl.uniform1i(this._numRegionsLocation, items.length);
  }
}

export class MosaicCircleProgram extends EffectProgram {
  private _positionsLocation: WebGLUniformLocation;
  private _sizesLocation: WebGLUniformLocation;
  private _densitiesLocation: WebGLUniformLocation;
  private _numRegionsLocation: WebGLUniformLocation;

  constructor(
    gl: WebGL2RenderingContext,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, mosaicCircleFragmentShaderSource, setupVao);
    this._positionsLocation = this._getUniform('u_positions');
    this._sizesLocation = this._getUniform('u_sizes');
    this._densitiesLocation = this._getUniform('u_densities');
    this._numRegionsLocation = this._getUniform('u_numRegions');
  }

  setupUniforms(
    items: ReadonlyArray<CCMosaicRect>,
    transformPosition: TransformFn,
    transformSize: TransformFn,
  ): void {
    const positions = new Float32Array(items.flatMap(transformPosition));
    const sizes = new Float32Array(items.flatMap(transformSize));
    const densities = new Float32Array(items.map((rect) => rect.density));
    this._gl.uniform2fv(this._positionsLocation, positions);
    this._gl.uniform2fv(this._sizesLocation, sizes);
    this._gl.uniform1fv(this._densitiesLocation, densities);
    this._gl.uniform1i(this._numRegionsLocation, items.length);
  }
}

abstract class BlurBaseProgram extends EffectProgram {
  private _positionsLocation: WebGLUniformLocation;
  private _sizesLocation: WebGLUniformLocation;
  private _radiiLocation: WebGLUniformLocation;
  private _kernelLocation: WebGLUniformLocation;
  private _maxRadiusLocation: WebGLUniformLocation;
  private _numRegionsLocation: WebGLUniformLocation;

  constructor(
    gl: WebGL2RenderingContext,
    fragmentSource: string,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, fragmentSource, setupVao);
    this._positionsLocation = this._getUniform('u_positions');
    this._sizesLocation = this._getUniform('u_sizes');
    this._radiiLocation = this._getUniform('u_radii');
    this._kernelLocation = this._getUniform('u_kernel');
    this._maxRadiusLocation = this._getUniform('u_maxRadius');
    this._numRegionsLocation = this._getUniform('u_numRegions');
  }

  setupUniforms(
    items: ReadonlyArray<CCBlurRect>,
    transformPosition: TransformFn,
    transformSize: TransformFn,
    calculateGaussianKernel?: (maxRadius: number) => Float32Array,
  ): void {
    const positions = new Float32Array(items.flatMap(transformPosition));
    const sizes = new Float32Array(items.flatMap(transformSize));
    const radii = new Int32Array(items.map((rect) => rect.sampleDistance));
    const radius = Math.max(...radii);
    const kernel = calculateGaussianKernel(radius);

    this._gl.uniform1i(this._maxRadiusLocation, radius);
    this._gl.uniform1fv(this._kernelLocation, kernel);
    this._gl.uniform2fv(this._positionsLocation, positions);
    this._gl.uniform2fv(this._sizesLocation, sizes);
    this._gl.uniform1iv(this._radiiLocation, radii);
    this._gl.uniform1i(this._numRegionsLocation, items.length);
  }
}

export class BlurRectProgram extends BlurBaseProgram {
  constructor(
    gl: WebGL2RenderingContext,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, blurRectFragmentShaderSource, setupVao);
  }
}

export class BlurCircleProgram extends BlurBaseProgram {
  constructor(
    gl: WebGL2RenderingContext,
    setupVao: (program: WebGLProgram) => WebGLVertexArrayObject,
  ) {
    super(gl, blurCircleFragmentShaderSource, setupVao);
  }
}
